/**
 * Sons de l'interface, entierement SYNTHETISES.
 *
 * Aucun fichier binaire, aucune licence a tracer, aucun octet a telecharger :
 * chaque effet est un petit graphe WebAudio (oscillateurs, bruit filtre,
 * enveloppes ADSR) construit a la volee puis detruit.
 *
 * Palette sonore assortie a la direction artistique : feutre, laiton, bois.
 * Rien de criard, pas de jingle de machine a sous — un casino calme.
 */

export type SfxName =
  | 'click'
  | 'hover'
  | 'coin'
  | 'win'
  | 'error'
  | 'whoosh'
  | 'lever'
  | 'reelTick'
  | 'reelStop'
  | 'lose';

export interface SfxContext {
  readonly ctx: AudioContext;
  /** Bus de sortie des effets (deja attenue par le volume "effets"). */
  readonly out: AudioNode;
  /** Instant de depart, en secondes dans l'horloge AudioContext. */
  readonly at: number;
}

interface EnvelopeOptions {
  readonly attack: number;
  readonly decay: number;
  readonly sustain: number;
  readonly release: number;
  readonly peak: number;
  readonly duration: number;
}

/** Enveloppe ADSR classique appliquee a un GainNode. */
function envelope(gain: GainNode, at: number, options: EnvelopeOptions): number {
  const { attack, decay, sustain, release, peak, duration } = options;
  const param = gain.gain;
  const hold = Math.max(0, duration - attack - decay - release);
  const end = at + attack + decay + hold + release;

  param.setValueAtTime(0.0001, at);
  param.exponentialRampToValueAtTime(Math.max(peak, 0.0002), at + attack);
  param.exponentialRampToValueAtTime(Math.max(peak * sustain, 0.0001), at + attack + decay);
  param.setValueAtTime(Math.max(peak * sustain, 0.0001), at + attack + decay + hold);
  param.exponentialRampToValueAtTime(0.0001, end);
  return end;
}

function tone(
  sfx: SfxContext,
  options: {
    readonly type: OscillatorType;
    readonly frequency: number;
    readonly glideTo?: number;
    readonly offset?: number;
    readonly envelope: EnvelopeOptions;
    readonly detune?: number;
  },
): number {
  const { ctx, out, at } = sfx;
  const start = at + (options.offset ?? 0);
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();

  osc.type = options.type;
  osc.frequency.setValueAtTime(options.frequency, start);
  if (options.glideTo !== undefined) {
    osc.frequency.exponentialRampToValueAtTime(
      Math.max(options.glideTo, 1),
      start + options.envelope.duration,
    );
  }
  if (options.detune !== undefined) osc.detune.setValueAtTime(options.detune, start);

  osc.connect(gain).connect(out);
  const end = envelope(gain, start, options.envelope);
  osc.start(start);
  osc.stop(end + 0.02);
  return end;
}

/** Buffer de bruit blanc, mis en cache par AudioContext. */
const noiseCache = new WeakMap<AudioContext, AudioBuffer>();

function noiseBuffer(ctx: AudioContext): AudioBuffer {
  const cached = noiseCache.get(ctx);
  if (cached) return cached;

  const length = Math.floor(ctx.sampleRate * 1.2);
  const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
  const channel = buffer.getChannelData(0);
  for (let i = 0; i < length; i += 1) channel[i] = Math.random() * 2 - 1;
  noiseCache.set(ctx, buffer);
  return buffer;
}

function noise(
  sfx: SfxContext,
  options: {
    readonly filter: BiquadFilterType;
    readonly frequency: number;
    readonly sweepTo?: number;
    readonly q?: number;
    readonly offset?: number;
    readonly envelope: EnvelopeOptions;
  },
): number {
  const { ctx, out, at } = sfx;
  const start = at + (options.offset ?? 0);

  const source = ctx.createBufferSource();
  source.buffer = noiseBuffer(ctx);
  source.loop = true;

  const filter = ctx.createBiquadFilter();
  filter.type = options.filter;
  filter.frequency.setValueAtTime(options.frequency, start);
  if (options.sweepTo !== undefined) {
    filter.frequency.exponentialRampToValueAtTime(
      Math.max(options.sweepTo, 20),
      start + options.envelope.duration,
    );
  }
  filter.Q.setValueAtTime(options.q ?? 1, start);

  const gain = ctx.createGain();
  source.connect(filter).connect(gain).connect(out);
  const end = envelope(gain, start, options.envelope);
  source.start(start);
  source.stop(end + 0.02);
  return end;
}

const CLICK_ENV: EnvelopeOptions = {
  attack: 0.002,
  decay: 0.02,
  sustain: 0.25,
  release: 0.05,
  peak: 0.35,
  duration: 0.08,
};

/** Jeton de bakelite pose sur le feutre : un clic mat, sans brillance. */
function click(sfx: SfxContext): void {
  tone(sfx, { type: 'triangle', frequency: 420, glideTo: 190, envelope: CLICK_ENV });
  noise(sfx, {
    filter: 'bandpass',
    frequency: 1800,
    q: 1.6,
    envelope: { attack: 0.001, decay: 0.015, sustain: 0.1, release: 0.03, peak: 0.14, duration: 0.05 },
  });
}

/** Effleurement : presque rien, juste un reperage. */
function hover(sfx: SfxContext): void {
  tone(sfx, {
    type: 'sine',
    frequency: 1320,
    envelope: { attack: 0.004, decay: 0.02, sustain: 0.2, release: 0.04, peak: 0.05, duration: 0.06 },
  });
}

/** Piece qui tombe : deux partiels metalliques desaccordes + attaque bruitee. */
function coin(sfx: SfxContext): void {
  tone(sfx, {
    type: 'triangle',
    frequency: 1046.5,
    envelope: { attack: 0.002, decay: 0.06, sustain: 0.18, release: 0.16, peak: 0.24, duration: 0.24 },
  });
  tone(sfx, {
    type: 'sine',
    frequency: 1567.98,
    detune: 8,
    offset: 0.012,
    envelope: { attack: 0.002, decay: 0.05, sustain: 0.12, release: 0.14, peak: 0.16, duration: 0.2 },
  });
  noise(sfx, {
    filter: 'highpass',
    frequency: 2600,
    envelope: { attack: 0.001, decay: 0.02, sustain: 0.08, release: 0.04, peak: 0.1, duration: 0.06 },
  });
}

/** Gain : accord ascendant bref, laiton chaud, jamais triomphaliste. */
function win(sfx: SfxContext): void {
  const notes = [523.25, 659.25, 783.99, 1046.5];
  notes.forEach((frequency, index) => {
    tone(sfx, {
      type: 'triangle',
      frequency,
      offset: index * 0.055,
      envelope: {
        attack: 0.006,
        decay: 0.08,
        sustain: 0.3,
        release: 0.22,
        peak: 0.2 - index * 0.02,
        duration: 0.3,
      },
    });
  });
}

/** Erreur : descente sourde, sans agressivite. */
function error(sfx: SfxContext): void {
  tone(sfx, {
    type: 'sawtooth',
    frequency: 233.08,
    glideTo: 116.54,
    envelope: { attack: 0.004, decay: 0.06, sustain: 0.35, release: 0.14, peak: 0.16, duration: 0.26 },
  });
}

/** Transition d'ecran : souffle filtre, comme une carte qui glisse. */
function whoosh(sfx: SfxContext): void {
  noise(sfx, {
    filter: 'bandpass',
    frequency: 320,
    sweepTo: 2400,
    q: 0.9,
    envelope: { attack: 0.03, decay: 0.06, sustain: 0.5, release: 0.14, peak: 0.12, duration: 0.24 },
  });
}

// ---------------------------------------------------------------------------
// Machines a sous
// ---------------------------------------------------------------------------

/** Levier : la detente mecanique, puis le ressort. Sec, jamais tonitruant. */
function lever(sfx: SfxContext): void {
  noise(sfx, {
    filter: 'bandpass',
    frequency: 900,
    sweepTo: 260,
    q: 2.2,
    envelope: { attack: 0.002, decay: 0.05, sustain: 0.3, release: 0.1, peak: 0.2, duration: 0.18 },
  });
  tone(sfx, {
    type: 'triangle',
    frequency: 196,
    glideTo: 88,
    envelope: { attack: 0.003, decay: 0.05, sustain: 0.3, release: 0.12, peak: 0.18, duration: 0.2 },
  });
}

/**
 * Cliquetis du ruban qui defile : un crantage tres bref et tres bas.
 * Il traduit une vitesse, il ne cree pas de tension — d'ou le niveau minuscule.
 */
function reelTick(sfx: SfxContext): void {
  noise(sfx, {
    filter: 'bandpass',
    frequency: 2300,
    q: 3.2,
    envelope: {
      attack: 0.001,
      decay: 0.008,
      sustain: 0.06,
      release: 0.014,
      peak: 0.055,
      duration: 0.024,
    },
  });
}

/** Arret d'un rouleau : le cran qui tombe, bois et laiton. */
function reelStop(sfx: SfxContext): void {
  tone(sfx, {
    type: 'triangle',
    frequency: 320,
    glideTo: 132,
    envelope: {
      attack: 0.001,
      decay: 0.035,
      sustain: 0.16,
      release: 0.07,
      peak: 0.26,
      duration: 0.12,
    },
  });
  noise(sfx, {
    filter: 'lowpass',
    frequency: 1400,
    sweepTo: 380,
    envelope: {
      attack: 0.001,
      decay: 0.022,
      sustain: 0.1,
      release: 0.05,
      peak: 0.16,
      duration: 0.08,
    },
  });
}

/**
 * Tour perdant : une seule note grave, sourde, vite eteinte.
 *
 * Volontairement plus discrete que `win` et sans la moindre agressivite. Une
 * perte n'a pas a etre punie sonorement : ce serait un levier d'engagement, pas
 * une information.
 */
function lose(sfx: SfxContext): void {
  tone(sfx, {
    type: 'sine',
    frequency: 174.61,
    glideTo: 146.83,
    envelope: { attack: 0.01, decay: 0.09, sustain: 0.28, release: 0.2, peak: 0.11, duration: 0.34 },
  });
}

export const SFX: Readonly<Record<SfxName, (sfx: SfxContext) => void>> = {
  click,
  hover,
  coin,
  win,
  error,
  whoosh,
  lever,
  reelTick,
  reelStop,
  lose,
};
